import { isEqual, startCase } from 'lodash';
import type { StoryMediaQuery } from 'types/story';
import { getMediaKeysInOrderOfInheritance } from 'common/utils/get-media-keys-in-order-of-inheritance';
import { getPlatformReferenceWidth, orderMediaQueryConfig } from 'common/utils/story-media-query';
import { generateId } from 'utils/generate-id';
import {
	DESIGN_PRESET_PROPERTIES,
	type DesignDefinition,
	type DesignPreset,
	type DesignPresetProperty,
	type DesignPresetPropertyValue,
} from './types';
import { createDesignTokenId } from './operations';
import {
	getDesignPresetTokenIds,
	isDesignPresetTokenCompatible,
	normalizeDesignPresetValue,
	validateDesignPresets,
} from './presets';
import type { DesignPresetStyles } from './preset-styles';

/** A building block's Default style as preset assignments, and what a preset cannot take from it */
export type DesignPresetCapture = {
	properties: DesignPreset['properties'];
	/** Other states with styles, such as Hover */
	states: string[];
	/** Default-state properties a preset does not support, such as width */
	unsupported: string[];
	/** Supported properties whose value a preset cannot hold, such as an `auto` margin or a missing token */
	invalid: DesignPresetProperty[];
};

const VW = /([-+]?\d*\.?\d+)vw\b/g;
const UNITLESS = /^[-+]?\d*\.?\d+$/;
const NUMERIC_PROPERTIES = new Set<string>(['lineHeight', 'opacity', 'fontWeight']);

const roundTo = (value: number, decimals: number) => Number(value.toFixed(decimals)) || 0;

/** A stored element value in the preset's terms: vw as px at the platform's reference width, rounded as its field */
function toPresetValue(property: string, value: unknown, width: number, decimals: number): unknown {
	if (typeof value !== 'string') return value;
	const trimmed = value.trim();
	if (NUMERIC_PROPERTIES.has(property) && UNITLESS.test(trimmed)) return Number(trimmed);
	return trimmed.replace(VW, (_, amount: string) => `${roundTo((parseFloat(amount) * width) / 100, decimals)}px`);
}

// Values that mean "nothing assigned here": an empty slot, or the marker of an unbound radius
const isUnassigned = (value: unknown) => value === undefined || value === null || value === '' || value === 'unset';

/**
 * Take what a building block shows in its Default state as preset assignments. `styles` are effective assignments
 * (preset, then local, then for a symbol master and instance), as stored. Each platform keeps the values it sets;
 * the default platform becomes Base, and a platform that repeats what it would inherit is left out. A converted px
 * value rounds to `decimals`, the precision of its Inspector field. Token references stay as they are.
 */
export function captureDesignPresetProperties({
	styles,
	design,
	mediaQuery,
	decimals,
}: {
	styles: DesignPresetStyles;
	/** The Story's resources: a value linking a token they lack, or a token of the wrong type, stays out */
	design: DesignDefinition | undefined;
	mediaQuery: StoryMediaQuery;
	decimals: number;
}): DesignPresetCapture {
	const tokens = new Map(
		design?.tokens.collections.flatMap(collection => collection.tokens.map(token => [token.id, token] as const))
	);
	const linksResolve = (property: string, value: unknown) =>
		getDesignPresetTokenIds(value).every(id => {
			const token = tokens.get(id);
			return !!token && isDesignPresetTokenCompatible(property, token.type);
		});
	const defaultState = styles.defaultState ?? {};
	const ordered = orderMediaQueryConfig(mediaQuery, { defaultFirst: true, minOrder: 'asc', maxOrder: 'desc' });
	const properties: DesignPreset['properties'] = {};
	const unsupported = new Set<string>();
	const invalid = new Set<DesignPresetProperty>();
	for (const { key: platform, config } of ordered) {
		const values = defaultState[platform] ?? {};
		const chain = getMediaKeysInOrderOfInheritance(mediaQuery.defaultPlatform, config, ordered).slice(0, -1);
		for (const [property, value] of Object.entries(values)) {
			if (isUnassigned(value)) continue;
			if (!DESIGN_PRESET_PROPERTIES.includes(property as DesignPresetProperty)) {
				unsupported.add(startCase(property));
				continue;
			}
			const key = property as DesignPresetProperty;
			const inherited = chain
				.map(source => defaultState[source]?.[property])
				.filter(item => !isUnassigned(item))
				.at(-1);
			// The same stored value as the platform it inherits from adds nothing
			if (platform !== mediaQuery.defaultPlatform && isEqual(inherited, value)) continue;
			const width = getPlatformReferenceWidth(mediaQuery, platform);
			let next: DesignPresetPropertyValue['base'];
			try {
				next = normalizeDesignPresetValue(key, toPresetValue(property, value, width, decimals));
			} catch {
				invalid.add(key);
				continue;
			}
			if (!linksResolve(property, next)) {
				invalid.add(key);
				continue;
			}
			const assignment: DesignPresetPropertyValue = properties[key] ?? {};
			if (platform === mediaQuery.defaultPlatform) assignment.base = next;
			else assignment.platforms = { ...assignment.platforms, [platform]: next };
			properties[key] = assignment;
		}
	}
	const states = Object.entries(styles)
		.filter(([state, platforms]) => state !== 'defaultState' && Object.values(platforms ?? {}).some(hasValues))
		.map(([state]) => startCase(state.replace(/State$/, '')));
	return {
		properties,
		states,
		unsupported: [...unsupported],
		invalid: [...invalid].filter(property => !properties[property]),
	};
}

const hasValues = (values: Record<string, unknown> | undefined) =>
	Object.values(values ?? {}).some(value => !isUnassigned(value));

/** A new Story preset from captured assignments, first in the library, as Add preset places one */
export function addDesignPresetFromCapture({
	design,
	name,
	description,
	properties,
}: {
	design: DesignDefinition;
	name: string;
	description?: string;
	properties: DesignPreset['properties'];
}): { design: DesignDefinition; preset: DesignPreset } {
	if (!name.trim()) throw new Error('A design preset requires a name.');
	if (!Object.keys(properties).length) throw new Error('Nothing to save: choose at least one property group.');
	const presets = design.presets ?? [];
	const [preset] = validateDesignPresets([
		{
			id: createDesignTokenId(
				`preset-${generateId(12)}`,
				presets.map(item => item.id),
				'preset'
			),
			name: name.trim(),
			...(description?.trim() ? { description: description.trim() } : {}),
			properties,
		},
	]);
	return { design: { ...design, presets: [preset, ...presets] }, preset };
}
